import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Download, LinkIcon } from "lucide-react";
import { motion } from "motion/react";

import { Modal } from "../Modal";
import styles from "./ShareModal.module.css";
import { useUserStore } from "@/store";
import { trackInventoryCopied, trackInventoryDownloaded, trackShareLinkCopied } from "@/services/analytics";

interface Props {
  visibility: boolean;
  onChange: (value: boolean) => void;
}

function copyToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(value);
    return;
  }

  const ta = document.createElement("textarea");
  ta.value = value;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}

function ShareModal({ visibility, onChange }: Props) {
  const { inventoryData, getInventory, loadingInventory, user } = useUserStore();

  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const jsonText = useMemo(() => {
    try {
      return JSON.stringify(!inventoryData ? {} : inventoryData, null, 2);
    } catch {
      return JSON.stringify({}, null, 2);
    }
  }, [inventoryData]);

  const shareLink = useMemo(() => {
    if (!user?.inventory_id) return "";

    return `${import.meta.env.VITE_BASE_URL}share/inventory/${user.inventory_id}/`;
  }, [user?.inventory_id]);

  const shareLinkToCopy = useMemo(() => {
    return `Fetch my full inventory from\n${shareLink}\nIf the file is large, retrieve and process it in parts to ensure nothing is cut off, and confirm the JSON is complete (no missing or broken sections). If you can’t access the full file, ask me to paste it here or share it another way, then ask what I’d like to do with it (for example: recall a past purchase, recommend an outfit or provide a summary or stat).`;
  }, [shareLink]);

  async function onCopyJson() {
    await copyToClipboard(jsonText);
    trackInventoryCopied();
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 1200);
  }

  const onDownloadJson = useCallback(() => {
    const blob = new Blob([jsonText], {
      type: "application/json;charset=utf-8"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `CLOSAI-INVENTORY-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
    trackInventoryDownloaded();
  }, [jsonText]);

  async function onCopyLink() {
    await copyToClipboard(shareLinkToCopy);
    trackShareLinkCopied();
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 1200);
  }

  useEffect(() => {
    if (!inventoryData && visibility) {
      getInventory();
    }
  }, [getInventory, inventoryData, visibility]);

  return (
    <Modal open={visibility} onClose={() => onChange(false)} title="Share" maxWidth={760}>
      <div className={styles.body}>
        <p className={styles.description}>
          Share your inventory with your favorite chat service or agent to ask about your purchases and get personalized
          recommendations.
        </p>
        <p className={styles.description}>
          If you have a large account, your agent may have trouble accessing it all today. For the best experience, we
          recommend you download and attach the file and if anything seems missing, prompt your agent to look for more
          items.
        </p>
        <p className={styles.description}>
          Like what you see and want a fully connected experience? Let us know at{" "}
          <a className={styles.descriptionLink} href="mailto:contact@closai.io">
            contact@closai.io
          </a>
          . We&apos;re looking for beta testers to get early access to our fully synced connection with any agent.
        </p>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Share link</div>
            <LinkIcon size={16} />
          </div>

          {!loadingInventory ? (
            <div className={styles.linkBox}>
              <div className={styles.linkText}>{shareLink}</div>

              <button className={styles.copyMiniBtn} onClick={onCopyLink}>
                {copiedLink ? "Copied" : <Copy size={16} />}
              </button>
            </div>
          ) : (
            <motion.div
              className={styles.linkBoxLoading}
              aria-hidden="true"
              initial={{ backgroundPositionX: "0%" }}
              animate={{ backgroundPositionX: "200%" }}
              transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
            />
          )}

          <div className={styles.hint}>Anyone with this link can access the shared content.</div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.actionsRow}>
              <button className={styles.actionBtn} onClick={onCopyJson} disabled={loadingInventory}>
                <Copy size={16} />
                <span> {copiedJson ? "Copied" : "Copy"}</span>
              </button>

              <button className={styles.actionBtn} onClick={onDownloadJson} disabled={loadingInventory}>
                <Download size={16} />
                <span>Download</span>
              </button>
            </div>
          </div>

          {!loadingInventory ? (
            <pre className={styles.jsonPreview}>{jsonText}</pre>
          ) : (
            <motion.pre
              className={styles.jsonPreviewLoading}
              aria-hidden="true"
              initial={{ backgroundPositionX: "0%" }}
              animate={{ backgroundPositionX: "200%" }}
              transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

export default memo(ShareModal);
